import { useState, useEffect, useRef } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useData } from "../contexts/DataContext";
import { toast } from "sonner";
import MessageBubble from "./chat/MessageBubble";
import ChatComposer from "./chat/ChatComposer";
import { FilePreviewModal } from "./chat/FilePreview";
import { useChatMessages } from "./chat/useChatMessages";
import { useMentionInput } from "./chat/useMentionInput";
import { useChatUpload } from "./chat/useChatUpload";
import { cn } from "../lib/utils";

export default function ChatPanel({ channel, className, testid = "chat" }) {
  const { user } = useAuth();
  const { users } = useData();
  const { messages, reload, editMessage, deleteMessage } = useChatMessages(channel, user?.id);
  const { text, resetText, mentionOpen, mentionQuery, onTextChange, insertMention, inputRef } = useMentionInput();
  const { pending, uploading, upload, removePending, clearPending } = useChatUpload(channel);
  const [preview, setPreview] = useState(null);
  const bottomRef = useRef(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages.length]);

  const mentionCandidates = mentionOpen
    ? users.filter((u) => u.id !== user?.id && u.name.toLowerCase().includes(mentionQuery.toLowerCase())).slice(0, 6)
    : [];

  const handleEdit = async (id, newText) => {
    try {
      const mentions = users.filter((u) => u.id !== user?.id && newText.includes(`@${u.name}`)).map((u) => u.id);
      await editMessage(id, newText, mentions);
      toast.success("Pesan diperbarui");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const handleDelete = async (id) => {
    try {
      await deleteMessage(id);
      toast.success("Pesan dihapus");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const send = async () => {
    if (!text.trim() && pending.length === 0) return;
    try {
      const mentions = users.filter((u) => u.id !== user?.id && text.includes(`@${u.name}`)).map((u) => u.id);
      await api.post("/chat/messages", { channel, text: text.trim(), mentions, attachments: pending });
      resetText();
      clearPending();
      reload();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className={cn("flex flex-col min-h-0", className)} data-testid={`${testid}-panel`}>
      <div className="flex-1 overflow-y-auto space-y-3 pr-1 py-1" data-testid={`${testid}-messages`}>
        {messages.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center">No messages yet. Start the conversation.</p>}
        {messages.map((m) => (
          <MessageBubble key={m.id} m={m} mine={m.user_id === user?.id} users={users} onPreview={setPreview} testid={testid}
            canModerate={user?.role === "admin"} onEdit={handleEdit} onDelete={handleDelete} />
        ))}
        <div ref={bottomRef} />
      </div>

      <ChatComposer text={text} onTextChange={onTextChange} onSend={send} onUpload={upload} uploading={uploading}
        pending={pending} onRemovePending={removePending} mentionCandidates={mentionCandidates}
        onInsertMention={insertMention} inputRef={inputRef} testid={testid} />

      {preview && <FilePreviewModal file={preview} onClose={() => setPreview(null)} />}
    </div>
  );
}
